import React, { useMemo, useState } from "react";
import { Button, Col, Form, Input, InputNumber, Modal, Pagination, Row, Select, Space, Table, Tag, Typography, message } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./CombustionTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { ResponseCompanyMapping } from "src/entity/company/company-mapping.entity";
import { usePagination } from "src/hook/usePagination";
import { useDidMount } from "src/hook/useDidMount";
import { settingStore } from "src/stores/setting-store";
import { useShallow } from "zustand/react/shallow";
import { FilterSettingCombustion, ResponseSettingCombustion, UpdateSettingCombustion } from "src/entity/settings/combustion";
import WrapCenter from "src/components/WrapCenter/WrapCenter";
import { PlusCircleOutlined } from "@ant-design/icons";
import { generateErrorMessage } from "src/utils/error-message";
import { Option } from "src/entity/settings/masterdata";
import VocsModal from "src/components/Modal";
import { uniqBy, omitBy, isNil } from "lodash";
import { onKeyDownNumberWithDecimal } from "src/utils/key-down-validate";
import { CommonSuccess } from "src/entity/common/menus-enum.enum";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import CustomInputNumber from "src/components/AntdCustomPart/InputNumber/CustomInputNumber";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import useNavigationMenu from "src/hook/useNavigationMenu";
import { canEditTable } from "./PermissionCheck/PermissionCheck";
import { calculateReportVocEmissionMasterChange } from "src/services/combustion.service";

const { Text } = Typography;
interface CombustionTableProps {}

const CombustionTable: React.FC<CombustionTableProps> = ({}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [formSettingCombustion] = Form.useForm();
  const [visibleModal, setVisibleModal] = useState(false);
  const getSettingCombustion = settingStore(useShallow((state) => state.getSettingCombustion));
  const dataSettingCombustion = settingStore(useShallow((state) => state.data));
  const totalDocs = settingStore(useShallow((state) => state.total));
  const [isEdit, setIsEdit] = useState<UpdateSettingCombustion | null>(null);
  const { permissions } = useNavigationMenu();

  const fuelTypeData = settingStore(useShallow((state) => state.fuelType));
  const emissionData = settingStore(useShallow((state) => state.emission));
  const units = settingStore(useShallow((state) => state.unit));

  const unitData = useMemo(() => {
    if (!units || !units.length) return [];
    return units[0].options.filter((item) => item.value.toLowerCase().includes("kg/l") || item.value.toLowerCase().includes("kg/m3"));
  }, [units]);
  const getFilterCombustionColumn = settingStore(useShallow((state) => state.getFilterCombustionColumn));
  const filterCombustion = settingStore(useShallow((state) => state.filterCombustion));
  const loadingManageCombustion = settingStore(useShallow((state) => state.loadingManageCombustion));
  const createSettingCombustion = settingStore(useShallow((state) => state.createSettingCombustion));
  const deleteSettingCombustion = settingStore(useShallow((state) => state.deleteSettingCombustion));
  const updateSettingCombustion = settingStore(useShallow((state) => state.updateSettingCombustion));
  const clearErrorSettingCombustion = settingStore(useShallow((state) => state.clearErrorSettingCombustion));
  const errorManaging = settingStore(useShallow((state) => state.errorManaging));

  const onClickEdit = (record: ResponseSettingCombustion) => {
    setIsEdit(record);
    setVisibleModal(true);
    formSettingCombustion.setFieldsValue({
      ...record,
    });
  };
  const deleteSetting = async (id: string) => {
    const confirm = await VocsModal.delete({
      message: "",
    });

    if (!confirm) return;
    deleteSettingCombustion(id).then(() => onFinishAction());
  };

  const {
    onChange,
    filter,
    page,
    setPage,
    loading,
    limit: pageSize,
    setLimit: setPageSize,
    data: sourceData,
  } = usePagination<ResponseSettingCombustion, FilterSettingCombustion>({
    action: getSettingCombustion,
    remoteData: dataSettingCombustion,
  });

  // console.log("unitData ; ", unitData);
  const getMasterLabel = (type: string, text: string) => {
    if (!text || typeof text != "string") return "";

    if (type === "emission" && emissionData && emissionData[0]) {
      const optValue = emissionData[0].options.find((item) => item.value === text);
      return (optValue as Option).label;
    } else if (type === "fuelType" && fuelTypeData && fuelTypeData[0]) {
      const optValue = fuelTypeData[0].options.find((item) => item.value === text);
      return (optValue as Option).label;
    } else if (type === "unit" && unitData && unitData[0]) {
      const optValue = unitData.find((item) => item.value.toLowerCase() === text.toLowerCase());
      return optValue ? (optValue.label === "Kg/m3" ? "Kg/m³" : optValue.label) : "";
    } else return text;
  };

  const fuelTypeFilter = useMemo(() => {
    if (!fuelTypeData || !fuelTypeData[0]) return [];
    return fuelTypeData[0].options.map((item) => {
      return {
        text: item.label,
        value: item.value,
      };
    });
  }, [fuelTypeData]);
  const emissionFactorFilter = useMemo(() => {
    return filterCombustion.emissionFactor.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterCombustion.emissionFactor]);
  const remarkFilter = useMemo(() => {
    return filterCombustion.remark.map((item) => {
      return {
        text: item,
        value: item,
      };
    });
  }, [filterCombustion.remark]);
  const emissionFilter = useMemo(() => {
    if (!emissionData || !emissionData[0]) return [];
    return emissionData[0].options.map((item) => {
      return {
        text: item.label,
        value: item.value,
      };
    });
  }, [emissionData]);
  const unitFilter = useMemo(() => {
    if (!unitData || !unitData[0]) return [];
    return unitData.map((item) => {
      return {
        text: item.label,
        value: item.value,
      };
    });
  }, [unitData]);

  const handleTableChange: TableProps<ResponseSettingCombustion>["onChange"] = (_, filters) => {
    onChange(filters);
  };

  const getIndex = (index: number) => (page - 1) * pageSize + index;
  const columns: TableProps<ResponseSettingCombustion>["columns"] = [
    {
      title: "No",
      key: "no",
      render: (_, __, index) => getIndex(index + 1),
      align: "center",
    },
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",
      render: (text) => <Text>{getMasterLabel("fuelType", text)}</Text>,
      filters: fuelTypeFilter,
      align: "left",
    },
    {
      title: "Emission",
      dataIndex: "emission",
      key: "emission",
      render: (text) => <Text>{getMasterLabel("emission", text)}</Text>,
      filters: emissionFilter,
      align: "left",
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      render: (text) => <Text>{getMasterLabel("unit", text)}</Text>,
      filters: unitFilter,
      align: "left",
    },
    {
      title: "Emission Factor",
      dataIndex: "emissionFactor",
      key: "emissionFactor",
      render: (text) => <Text>{text}</Text>,
      filters: emissionFactorFilter,
      align: "left",
    },
    {
      title: "Remark",
      dataIndex: "remark",
      key: "remark",
      render: (text) => <Text>{text}</Text>,
      filters: remarkFilter,
      align: "left",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => onClickEdit(record)}
            disabled={!canEditTable(permissions)}
          >
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteSetting(record._id)}
            disabled={!canEditTable(permissions)}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "center",
    },
  ];

  useDidMount(() => {
    getSettingCombustion({ ...filter, page, limit: pageSize });
    getFilterCombustionColumn("emissionFactor");
    getFilterCombustionColumn("remark");
  });

  const onChangePage = (val: number) => {
    setPage(val);
  };

  const paginationProps: PaginationProps = {
    size: "small",
    pageSize: pageSize,
    current: page,
    total: totalDocs || 0,
    defaultCurrent: 1,
    onChange: onChangePage,
    showSizeChanger: false,
  };

  const onAddUser = () => {
    setVisibleModal(true);
  };

  const closeModal = () => {
    setVisibleModal(false);
    formSettingCombustion.resetFields();
    clearErrorSettingCombustion();
    setIsEdit(null);
  };

  const submitSettingCombustion = async () => {
    let checkError = false;
    await formSettingCombustion.validateFields().catch(() => {
      checkError = true;
    });
    if (checkError) return;

    const formData = formSettingCombustion.getFieldsValue();
    // console.log("form data ; ", formData);
    const finalData = {
      ...formData,
    };

    if (isEdit) {
      updateSettingCombustion({ ...finalData, _id: isEdit._id }).then(onFinishAction);
    } else createSettingCombustion(finalData).then(onFinishAction);

    processCalculateReport(finalData.fuelType, finalData.emission);
  };

  const processCalculateReport = async (fuelType: string, emission: string) => {
    try {
      await calculateReportVocEmissionMasterChange({ fuelType, emission });
    } catch (error) {
      console.error(error);
    }
  };

  const onFinishAction = (errorMessage?: unknown) => {
    // console.log("errorMessage : ", errorMessage);
    if (errorMessage) {
      formSettingCombustion.setFields([
        {
          name: "emission",
          errors: [""],
        },
        {
          name: "fuelType",
          errors: [""],
        },
      ]);
      return;
    } else {
      messageApi.open({
        type: "success",
        content: CommonSuccess.SAVED_SUCCESSFULL,
      });
      getSettingCombustion({ ...filter, page, limit: pageSize });
      getFilterCombustionColumn("emissionFactor");
      getFilterCombustionColumn("remark");
      closeModal();
    }
  };

  return (
    <Row className="w-full" id={"wrap-combustion-config-table"}>
      {contextHolder}
      {canEditTable(permissions) && (
        <Row className="w-full" justify={"end"}>
          <Button type={"primary"} size={"middle"} className="flex items-center" onClick={onAddUser} disabled={!canEditTable(permissions)}>
            <PlusCircleOutlined />
            New
          </Button>
        </Row>
      )}
      <Table
        columns={columns}
        dataSource={sourceData as ResponseSettingCombustion[]}
        className="w-full"
        loading={loading}
        pagination={false}
        onChange={handleTableChange}
      />

      <Row className="w-full mt-4" justify={"end"}>
        <Pagination {...paginationProps} />
      </Row>

      <Modal
        title={<ModalTitle title="Emission Factor" />}
        open={visibleModal}
        width={1000}
        onCancel={closeModal}
        footer={
          <Row className="w-full border-t" justify={"end"}>
            <Button className="mt-4" key="submit" type="primary" loading={loadingManageCombustion} onClick={submitSettingCombustion}>
              Save
            </Button>
          </Row>
        }
        maskClosable={false}
        centered
      >
        {errorManaging && (
          <Row className="w-full" justify={"center"}>
            <Text type="danger">{errorManaging}</Text>
          </Row>
        )}

        <Form
          layout={"vertical"}
          name={"setting-combustion"}
          autoComplete="off"
          form={formSettingCombustion}
          requiredMark={false}
          onFieldsChange={(_) => {
            if (errorManaging) {
              clearErrorSettingCombustion();
              formSettingCombustion.setFields([
                {
                  name: "emission",
                  errors: undefined,
                },
                {
                  name: "fuelType",
                  errors: undefined,
                },
              ]);
            }
          }}
        >
          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Fuel Type"} required />}
                name="fuelType"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Fuel Type"),
                  },
                ]}
              >
                <Select>
                  {fuelTypeData.length > 0 &&
                    fuelTypeData[0].options.map((item, index) => (
                      <Select.Option key={"fuel-type" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Emission"} required />}
                name="emission"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Emission"),
                  },
                ]}
              >
                <Select>
                  {emissionData.length > 0 &&
                    emissionData[0].options.map((item, index) => (
                      <Select.Option key={"emission" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Unit"} required />}
                name="unit"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Unit"),
                  },
                ]}
              >
                <Select>
                  {unitData.length > 0 &&
                    unitData.map((item, index) => (
                      <Select.Option key={"unit" + index} value={item.value}>
                        {item.label}
                      </Select.Option>
                    ))}
                </Select>
              </Form.Item>
            </Col>

            <Col xs={24} sm={12} lg={12}>
              <Form.Item
                label={<FormLabel label={"Emission Factor"} required />}
                name="emissionFactor"
                rules={[
                  {
                    required: true,
                    message: generateErrorMessage("Emission Factor"),
                  },
                ]}
              >
                <CustomInputNumber
                  onChange={(e) => formSettingCombustion.setFieldValue("emissionFactor", !isNil(e) ? parseFloat(`${e}`).toFixed(10) : null)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={12}>
              <Form.Item label="Remark" name="remark">
                <Input />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Row>
  );
};

export default CombustionTable;
// 1,122.555666788
// 9999.2222
